import React, { useState } from 'react';
import { usePage } from '../../hooks/usePage';
import { useAuth } from '../../hooks/useAuth';
import { FeeConfig } from './sections/FeeConfig';
import { TierLimits } from './sections/TierLimits';
import { BusinessTierLimits } from './sections/BusinessTierLimits';
import { PremiumConfig } from './sections/PremiumConfig';
import { SystemConfig } from './sections/SystemConfig';
import { Receipt, Layers, Star, Monitor, Building2 } from 'lucide-react';
import { LucideIcon } from 'lucide-react';

type Tab = 'fees' | 'tiers' | 'business-tiers' | 'premium' | 'system';

const tabs: { key: Tab; label: string; icon: LucideIcon; roles?: string[] }[] = [
  { key: 'fees', label: 'Fee Config', icon: Receipt },
  { key: 'tiers', label: 'Tier Limits', icon: Layers },
  // Its own tab rather than a second table under "Tier Limits": the two
  // ladders answer different regulatory questions, and one screen editing
  // both is how a change meant for consumers reprices every business.
  { key: 'business-tiers', label: 'Business Tiers', icon: Building2 },
  { key: 'premium', label: 'Premium', icon: Star },
  { key: 'system', label: 'System', icon: Monitor, roles: ['super_admin'] },
];

export const Config: React.FC = () => {
  usePage('Configuration');
  const { admin } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('fees');

  const visibleTabs = tabs.filter(
    (t) => !t.roles || (admin && t.roles.includes(admin.role)),
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div
        style={{
          display: 'flex',
          gap: 2,
          borderBottom: '1px solid var(--neutral-300)',
          paddingBottom: 0,
        }}
      >
        {visibleTabs.map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                    padding: '10px 20px',
                    fontSize: 14,
                    fontWeight: 500,
                    fontFamily: 'var(--font-family)',
                    color: isActive ? 'var(--blue-600)' : 'var(--neutral-600)',
                    background: 'transparent',
                    borderBottom: isActive ? '2px solid var(--blue-600)' : '2px solid transparent',
                    marginBottom: -1,
                    transition: 'all 150ms ease',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                }}
                onMouseEnter={(e) => {
                    if (!isActive) e.currentTarget.style.color = 'var(--neutral-800)';
                }}
                onMouseLeave={(e) => {
                    if (!isActive) e.currentTarget.style.color = 'var(--neutral-600)';
                }}
                >
                <tab.icon size={16} />
                {tab.label}
            </button>
          );
        })}
      </div>

      <div>
        {activeTab === 'fees' && <FeeConfig />}
        {activeTab === 'tiers' && <TierLimits />}
        {activeTab === 'business-tiers' && <BusinessTierLimits />}
        {activeTab === 'premium' && <PremiumConfig />}
        {activeTab === 'system' && <SystemConfig />}
      </div>
    </div>
  );
};
